import { useEffect } from "react";

import i18n, { SUPPORTED_LANGUAGES } from "@/i18n";
import {
  hasAppliedUiLocales,
  hasStoredLanguage,
  markUiLocalesApplied,
  resolveUiLocales,
  storeLanguage,
} from "@/utils/languageStorage";

type RawLoginContext = {
  transaction?: { state?: string };
  untrusted_data?: { authorization_params?: { ui_locales?: string } };
};

/**
 * Reads an explicit `ui_locales` request: the current URL first (e.g. a link
 * edited to `ui_locales=en`), then the original /authorize params.
 */
function getRequestedUiLocales(): { uiLocales: string | null; state: string } {
  if (typeof window === "undefined") return { uiLocales: null, state: "" };

  const context = window.universal_login_context as unknown as
    | RawLoginContext
    | undefined;
  const fromUrl = new URLSearchParams(window.location.search).get("ui_locales");

  return {
    uiLocales:
      fromUrl ??
      context?.untrusted_data?.authorization_params?.ui_locales ??
      null,
    state: context?.transaction?.state ?? "",
  };
}

/**
 * Chooses the UI language for an Auth0 screen, in priority order:
 * 1. An explicit `ui_locales` request, applied once per transaction and value and
 *    then stored as the user's preference.
 * 2. The user's stored preference. Every ACUL screen is a fresh page load and
 *    `transaction.locale` does not follow the in-page language switch, so it must
 *    not reset the user's choice on each screen change.
 * 3. The Auth0 transaction locale, when supported.
 */
export function useAuth0Locale(locale: string | undefined): void {
  useEffect(() => {
    const { uiLocales, state } = getRequestedUiLocales();
    const requested = resolveUiLocales(uiLocales);
    if (requested) {
      const requestKey = `${state}:${requested}`;
      if (!hasAppliedUiLocales(requestKey)) {
        markUiLocalesApplied(requestKey);
        storeLanguage(requested);
        void i18n.changeLanguage(requested);
        return;
      }
    }

    if (
      !SUPPORTED_LANGUAGES.includes(
        locale as (typeof SUPPORTED_LANGUAGES)[number]
      )
    )
      return;
    if (hasStoredLanguage()) return;
    void i18n.changeLanguage(locale!);
  }, [locale]);
}
